import { type Component } from "vue";
import {
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from "vue-router";
import { Role } from "core-fe-lib/openapi/core/models/Role";
import { useLmsCapabilitiesStore } from "@/modules/lms/stores/lms-capabilities";

// Gate the editor space on the canAuthor capability (ADR 023) rather than a
// role: admins and editor-grant holders pass, everyone else is bounced to the
// learner catalog. The server enforces the same gate on every authoring call;
// this is the UX guard.
async function requireAuthor(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const caps = useLmsCapabilitiesStore();
  await caps.ensureLoaded();
  if (caps.canAuthor) next();
  else next({ name: "lms-catalog" });
}

export function lmsRoutes(MainLayout: Component): RouteRecordRaw {
  return {
    path: "/lms",
    component: MainLayout,
    children: [
      {
        path: "",
        component: () =>
          import("core-fe-lib/components-shadcn/layouts/PassthroughLayout.vue"),
        children: [
          // ----- Public (anonymous; tenant opt-in via lmsPublicCatalog) -----
          {
            path: "browse",
            name: "lms-public-catalog",
            component: () =>
              import("@/modules/lms/pages/public/PublicCatalog.vue"),
            meta: { requiresAuth: false },
          },
          {
            path: "browse/:slug",
            name: "lms-public-course",
            component: () =>
              import("@/modules/lms/pages/public/PublicCourseDetail.vue"),
            meta: { requiresAuth: false },
          },
          // Public share link — a tokenised, read-only learner view of a
          // published course. The token is the capability; no auth gate.
          {
            path: "s/:token",
            name: "lms-shared-course",
            component: () =>
              import("@/modules/lms/pages/public/PublicSharedCourse.vue"),
            meta: { requiresAuth: false },
          },
          // Print/PDF projection of a shared course — every slide laid out flat
          // as a full-bleed page for the browser's "Save as PDF". Reuses the
          // same renderer; auto-opens the print dialog.
          {
            path: "s/:token/print",
            name: "lms-shared-course-print",
            component: () =>
              import("@/modules/lms/pages/public/PublicSharedCoursePrint.vue"),
            meta: { requiresAuth: false },
          },
          // Author space — a per-author public "workspace" at a nice URL the
          // author can advertise (/lms/by/{handle}), listing their published
          // catalogs. The author's own publish flag gates it; no auth.
          {
            path: "by/:handle",
            name: "lms-author-space",
            component: () =>
              import("@/modules/lms/pages/public/PublicAuthorSpace.vue"),
            meta: { requiresAuth: false },
          },
          {
            path: "by/:handle/:catalogSlug",
            name: "lms-public-catalog-detail",
            component: () =>
              import("@/modules/lms/pages/public/PublicCatalogDetail.vue"),
            meta: { requiresAuth: false },
          },
          {
            path: "by/:handle/c/:courseSlug",
            name: "lms-public-author-course",
            component: () =>
              import("@/modules/lms/pages/public/PublicAuthorCourse.vue"),
            meta: { requiresAuth: false },
          },
          // Live-poll voter page — the QR target, scanned by anonymous course
          // participants. Lives under /lms/p/:joinCode so it rides the same
          // proven public path as the catalog (no app-shell auth gate).
          {
            path: "p/:joinCode",
            name: "lms-poll-vote",
            component: () =>
              import("@/modules/lms/pages/public/PublicPoll.vue"),
            meta: { requiresAuth: false },
          },
          // Phone-as-remote control surface — target of the presenter's private
          // QR (shown only on the console). The code is the secret; no auth gate.
          {
            path: "remote/:code",
            name: "lms-remote",
            component: () =>
              import("@/modules/lms/pages/public/RemoteControl.vue"),
            meta: { requiresAuth: false },
          },
          // ----- Learner -----
          {
            path: "",
            name: "lms-catalog",
            component: () => import("@/modules/lms/pages/LmsCatalog.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "courses/:slug",
            name: "lms-course-detail",
            component: () => import("@/modules/lms/pages/CourseDetail.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "my-learning",
            name: "lms-my-learning",
            component: () => import("@/modules/lms/pages/MyLearning.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "review",
            name: "lms-review",
            component: () => import("@/modules/lms/pages/ReviewQueue.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "learn/:courseId/:lessonId",
            name: "lms-classroom",
            component: () => import("@/modules/lms/pages/Classroom.vue"),
            meta: { requiresAuth: true },
          },
          // Audience window — the clean, projected slide. Opened by the
          // presenter in a second window; mirrors the presenter's position over
          // a BroadcastChannel. Covers the app chrome with a fixed overlay.
          {
            path: "present/audience",
            name: "lms-audience",
            component: () =>
              import("@/modules/lms/pages/AudiencePresenterWindow.vue"),
            meta: { requiresAuth: true },
          },
          // Manage editors — admin-tier action (granting authorship), so it
          // stays role-gated rather than canAuthor-gated.
          {
            path: "admin/editors",
            name: "lms-admin-editors",
            component: () =>
              import("@/modules/lms/pages/admin/AdminEditors.vue"),
            meta: {
              requiresAuth: true,
              requiredPrivilege: Role.CUSTOMER_ADMIN,
            },
          },
          {
            path: "admin/assets",
            name: "lms-admin-assets",
            component: () =>
              import("@/modules/lms/pages/admin/AdminAssetsPage.vue"),
            meta: { requiresAuth: true, requiredPrivilege: Role.SUPER_ADMIN },
          },
          // ----- Editor space (gated on canAuthor capability — ADR 023) -----
          // Nested under LmsEditorLayout so the instant-draft build panel
          // persists across list ↔ course-edit ↔ lesson-editor navigation.
          {
            path: "admin/courses",
            component: () =>
              import("@/modules/lms/pages/admin/LmsEditorLayout.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
            children: [
              {
                path: "",
                name: "lms-admin-courses",
                component: () =>
                  import("@/modules/lms/pages/admin/AdminCourseList.vue"),
              },
              {
                path: ":courseId",
                name: "lms-admin-course-edit",
                component: () =>
                  import("@/modules/lms/pages/admin/AdminCourseEdit.vue"),
              },
              {
                path: ":courseId/lessons/:lessonId",
                name: "lms-lesson-editor",
                component: () =>
                  import("@/modules/lms/pages/admin/LessonEditor.vue"),
              },
              {
                path: ":courseId/poll",
                name: "lms-poll-console",
                component: () =>
                  import("@/modules/lms/pages/admin/PollConsole.vue"),
              },
              {
                path: ":courseId/poll/dashboard",
                name: "lms-poll-dashboard",
                component: () =>
                  import("@/modules/lms/pages/admin/PollDashboard.vue"),
              },
            ],
          },
          // ----- Author space management (gated on canAuthor — ADR 023) -----
          // The author's own workspace editor: their public profile (handle +
          // branding) and the curated catalogs they publish under it.
          {
            path: "admin/space",
            name: "lms-admin-space",
            component: () =>
              import("@/modules/lms/pages/admin/AuthorSpace.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
          },
          {
            path: "admin/space/catalogs/:catalogId",
            name: "lms-space-catalog",
            component: () =>
              import("@/modules/lms/pages/admin/CatalogEditor.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
          },
          // Course-assistant run diagnostics. The aiemployee module was
          // retired, but the Course Author assistant still produces runs;
          // these two pages let an author inspect/replay a hung or failed
          // run (the "Open run" deep link on the chat timeout card). Route
          // names are kept as `aiemployee-*` so the restored pages — which
          // navigate between themselves — work unmodified. Author-gated,
          // same as the rest of the editor space.
          {
            path: "admin/runs",
            name: "aiemployee-runs-list",
            component: () => import("@/modules/aiemployee/pages/RunsPage.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
          },
          {
            path: "admin/runs/:id",
            name: "aiemployee-run-detail",
            component: () =>
              import("@/modules/aiemployee/pages/RunDetailPage.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
          },
          // Minimal AI-employee admin: list employees and change one's LLM
          // model (e.g. repoint the Course Author off an unreachable private
          // model). The rest of the aiemployee admin module stays retired.
          {
            path: "admin/employees",
            name: "aiemployee-employees-list",
            component: () =>
              import("@/modules/aiemployee/pages/EmployeeListPage.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
          },
          {
            path: "admin/employees/:id",
            name: "aiemployee-employee-edit",
            component: () =>
              import("@/modules/aiemployee/pages/EmployeeEditPage.vue"),
            meta: { requiresAuth: true },
            beforeEnter: requireAuthor,
          },
        ],
      },
    ],
  };
}
